import { graphql, PageProps } from "gatsby";
import React from "react";
import Breadcrumbs from "../components/Breadcrumbs";
import Button, { ButtonSize } from "../components/Buttons/Button";
import ContactDetailsBlock from "../components/ContactDetailsBlock";
import DocumentHead from "../components/DocumentHead/DocumentHead";
import FormSection from "../components/FormSection";
import Hero from "../components/Hero";
import Layout from "../components/Layout";
import { MainPageHeading } from "../components/MainPageHeading";
import MunicipalityList from "../components/MunicipalityList";
import NearbyOfficeList from "../components/Office/NearbyOfficeList";
import PortableText from "../components/PortableText";
import { PortableTextColorTheme } from "../components/PortableText/PortableText";
import { RelatedCards } from "../components/RelatedCards";
import { RelatedList } from "../components/RelatedList";
import { ReviewsBlock } from "../components/ReviewsBlock";
import useTranslations from "../hooks/useTranslations";
import { getSameOfficeServices } from "../mappers/locatorServiceInterlinkingMapper";
import { GatsbyPageContext } from "../models/pages/GatsbyPageContext";
import { isNullOrWhiteSpace } from "../shared/string";
import { formatPhoneNumberLink, isValidLink } from "../utilities/links";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const OfficePage = ({ data, pageContext }: PageProps<any, GatsbyPageContext>) => {
  const {
    office,
    relatedLocators,
    officesInSameRegion: officesInSameRegionRaw,
    sanitySiteSettings
  } = data;

  const translations = useTranslations();

  if (office) {
    const ctaLink = isValidLink(office.ctaLinkUrl)
      ? office.ctaLinkUrl
      : {
          isExternalLink: true,
          url: "#form"
        };

    const relatedServicesAndLocators = getSameOfficeServices({
      locators: relatedLocators?.nodes ?? [],
      relatedOffice: office,
      siteSettings: sanitySiteSettings
    });

    // Offices within the same region
    const officesInSameRegion = officesInSameRegionRaw?.nodes?.map(office => ({
      id: office._id,
      title: office.title
    }));

    return (
      <Layout
        pageContext={pageContext}
        hero={<Hero image={office.heroImage} titleOverlay={office.title} />}
        breadcrumbs={<Breadcrumbs startNode={office} />}
      >
        <DocumentHead
          pageData={{ ...office, relatedPageData: { relatedServicesAndLocators } }}
        />

        {!office.heroImage && <MainPageHeading>{office.title}</MainPageHeading>}

        {(!isNullOrWhiteSpace(office.preambleTitle) ||
          !isNullOrWhiteSpace(office.preambleText)) && (
          <aside className="flex flex-wrap lg:flex-nowrap justify-center xl:justify-between">
            <div className="min-w-sm-padding w-full xl:w-9/12 flex flex-col lg:pr-8 mb-8">
              {!isNullOrWhiteSpace(office.preambleTitle) && (
                <h2 className="mb-3 lg:mb-4">{office.preambleTitle}</h2>
              )}

              {!isNullOrWhiteSpace(office.preambleText) && (
                <p className="text-lg lg:text-2xl">{office.preambleText}</p>
              )}

              {!isNullOrWhiteSpace(office.formLinkButtonText) && (
                <div className="flex flex-col sm:items-center p-12 pb-8 xl:p-16 xl:pb-0 xl:items-stretch xl:flex-row">
                  {!isNullOrWhiteSpace(office.formLinkButtonText) && (
                    <Button
                      color={"primary"}
                      size={ButtonSize.Normal}
                      type="link"
                      {...ctaLink}
                    >
                      {office.formLinkButtonText}
                    </Button>
                  )}

                  {office.introPhoneNumber &&
                    !isNullOrWhiteSpace(office.introPhoneNumber) && (
                      <div className="flex flex-col justify-center mt-6 xl:mt-0 xl:ml-12 text-brand-primary-20 font-bold text-center">
                        <div className="uppercase text-sm">{translations.orCall}</div>
                        <div className="text-2xl">
                          <a
                            href={formatPhoneNumberLink(office.introPhoneNumber)}
                            className="text-current no-underline hover:text-brand-primary-40 hover:underline focus:outline-none focus:shadow-outline-primary"
                          >
                            {office.introPhoneNumber}
                          </a>
                        </div>
                      </div>
                    )}
                </div>
              )}
            </div>

            {office.contact && (
              <div className="flex min-w-sm w-full -mx-4 lg:mx-0 lg:w-auto">
                <ContactDetailsBlock {...office.contact} />
              </div>
            )}
          </aside>
        )}

        {!!relatedServicesAndLocators.length &&
          !isNullOrWhiteSpace(translations.relatedServicesAndLocatorsTitle) && (
            <RelatedCards
              cards={relatedServicesAndLocators}
              subtitle={translations.relatedServicesAndLocatorsSubtitle ?? undefined}
              title={translations.relatedServicesAndLocatorsTitle}
            />
          )}

        {office._rawPortableTextContent && (
          <PortableText
            _rawBody={office._rawPortableTextContent}
            colorTheme={PortableTextColorTheme.Neutral}
          />
        )}

        {!isNullOrWhiteSpace(office.municipalitiesTitle) && (
          <MunicipalityList
            title={office.municipalitiesTitle}
            municipalities={office.municipalities}
          />
        )}

        {office.nearbyOffices?.length > 0 && (
          <div className="text-brand-primary-20">
            <NearbyOfficeList
              title={office.nearbyOfficesTitle}
              subtitle={office.nearbyOfficesSubtitle}
              offices={office.nearbyOffices}
              portableTextColorThemeProps={{
                elementClasses: {
                  h3Subheader: "text-brand-primary-dim"
                }
              }}
            />
          </div>
        )}

        {!!officesInSameRegion?.length &&
          !isNullOrWhiteSpace(translations.officesInSameRegionTitle) && (
            <RelatedList
              items={officesInSameRegion}
              subtitle={translations.officesInSameRegionSubtitle ?? undefined}
              title={translations.officesInSameRegionTitle ?? undefined}
            />
          )}

        <FormSection
          forms={office.forms}
          stepByStepTitle={office.stepByStepTitle}
          stepBySteps={office.stepBySteps}
          title={office.contactFormTitle}
        />

        {(sanitySiteSettings?.displayReviewBlockOnAllOfficePages ||
          office?.reviewPlace?.googleBusinessAccountId) && (
          <ReviewsBlock
            text={office?.reviewBlock?.text}
            title={office?.reviewBlock?.title}
            placeId={office?.reviewPlace?.googleBusinessAccountId}
          />
        )}
      </Layout>
    );
  } else {
    return null;
  }
};

export const query = graphql`
  query GetOffice(
    $_id: String!
    $slugMatchRegex: String!
    $i18nLang: String!
    $hasRegionId: Boolean!
    $regionId: String
  ) {
    office: sanityOffice(_id: { eq: $_id }) {
      _id
      _type
      title
      preambleTitle
      preambleText
      publishDate
      reviewBlock {
        text
        title
      }
      reviewPlace {
        googleBusinessAccountId
      }
      ctaLinkUrl {
        ...SanityInlineLinkFragment
      }
      formLinkButtonText
      introPhoneNumber
      slug {
        current
      }
      _rawParentLink(resolveReferences: { maxDepth: 3 })
      heroImage {
        ...SanityImageExtFragment
      }
      _rawPortableTextContent(resolveReferences: { maxDepth: 7 })
      contact {
        officeHours {
          days
          openingHours
        }
        phoneNumber
        title
        address
        visitorAddress
      }
      location {
        lat
        lng
        alt
      }
      municipalitiesTitle
      municipalities
      nearbyOfficesTitle
      nearbyOfficesSubtitle
      nearbyOffices {
        _id
        title
        preambleText
        contact {
          officeHours {
            days
            openingHours
          }
          phoneNumber
          address
        }
        thumbnail {
          ...SanityImageExtFragment
        }
        _type
        slug {
          current
        }
        _rawParentLink(resolveReferences: { maxDepth: 3 })
        i18nLang
      }
      contactFormTitle
      forms {
        ...SanityFormTemplateFragment
        ...SanityPriceCalculatorFormTemplateFragment
      }
      referenceToRegion {
        title
      }
      stepByStepTitle
      stepBySteps
      servicesTitle
      servicesSubtitle
      # Using _raw variant here due to type issues when we started allowing multiple types in this field
      _rawReferenceToServices(resolveReferences: { maxDepth: 5 })
      seo {
        ...SanitySeoPropertiesFragment
      }
      socialMedia {
        ...SanitySocialMediaPropertiesFragment
      }
    }

    officesInSameRegion: allSanityOffice(
      filter: { _id: { ne: $_id }, referenceToRegion: { _id: { eq: $regionId } } }
      sort: { title: ASC }
    ) @include(if: $hasRegionId) {
      nodes {
        _id
        title
      }
    }

    relatedLocators: allSanityLocator(
      filter: {
        i18nLang: { eq: $i18nLang }
        slug: { current: { regex: $slugMatchRegex } }
      }
    ) {
      nodes {
        _id
        _rawParentLink
        heroImage {
          ...SanityImageExtFragment
        }
        title
      }
    }

    sanitySiteSettings(i18nLang: { eq: $i18nLang }) {
      defaultServices {
        _id
        _type
        thumbnail {
          ...SanityImageExtFragment
        }
        title
      }
      displayReviewBlockOnAllOfficePages
    }
  }
`;

export default OfficePage;
